import { AllVisitsPageNS } from '@/pages/AllVisitsPage/AllVisitsPage.types';
import { VisitState, VisitWorkflowStep } from '@/types/graphql';

export const getDefaultSelectedSecondaryTabs = (
    lowerTabs?: Set<AllVisitsPageNS.SecondaryTabConfigSet> | null,
): VisitWorkflowStep[] => {
    const tabArray = lowerTabs ? Array.from(lowerTabs) : [];
    return tabArray.filter(([, isDefaultSelected]) => isDefaultSelected).map(([title]) => title);
};

export const parseSecondaryTabsFromUrl = (urlParam: string | null): VisitWorkflowStep[] => {
    if (!urlParam) {
        return [];
    }

    return urlParam.split(',').map(decodeURIComponent) as VisitWorkflowStep[];
};

export const isVisitState = (
    primaryTab: AllVisitsPageNS.PrimaryTab,
): primaryTab is Exclude<AllVisitsPageNS.PrimaryTab, 'NEEDS_ASSIGNMENT' | 'NEEDS_ATTENTION'> =>
    Object.values(VisitState).includes(primaryTab as VisitState);

export const isVisitRequest = (
    item?: AllVisitsPageNS.TableRow,
): item is AllVisitsPageNS.VisitRequestCell => item?.__typename === 'VisitRequest';

export const isNeedsAttentionVisitRequest = (
    item?: AllVisitsPageNS.TableRow,
): item is AllVisitsPageNS.AllNeedingAttentionVisitRequests[number] =>
    item?.__typename === 'VisitRequest' &&
    typeof (item as AllVisitsPageNS.AllNeedingAttentionVisitRequests[number]).warnings !==
        'undefined';

export const isNeedsAttentionVisit = (
    item?: AllVisitsPageNS.TableRow,
): item is AllVisitsPageNS.AllNeedingAttentionVisits[number] =>
    item?.__typename === 'Visit' &&
    typeof (item as AllVisitsPageNS.AllNeedingAttentionVisits[number]).warnings !== 'undefined';

export const isAssignmentVisitRequest = (
    item?: AllVisitsPageNS.TableRow,
): item is AllVisitsPageNS.AllNeedingAssignmentItems[number] =>
    item?.__typename === 'VisitRequest' &&
    typeof (item as AllVisitsPageNS.AllNeedingAttentionVisitRequests[number]).warnings ===
        'undefined';

export const isStateVisit = (
    item?: AllVisitsPageNS.TableRow,
): item is AllVisitsPageNS.AllVisitsByStateItems[number] =>
    item?.__typename === 'Visit' &&
    typeof (item as AllVisitsPageNS.AllNeedingAttentionVisits[number]).warnings === 'undefined';

export const isVisit = (item?: AllVisitsPageNS.TableRow): item is AllVisitsPageNS.VisitCell =>
    item?.__typename === 'Visit';

export const getDate = (item: AllVisitsPageNS.AllNeedingAttentionItems[number]) => {
    if (isVisit(item)) {
        return item.effectiveDate ?? null;
    }
    if (isVisitRequest(item)) {
        return item.tentativeDate ?? item.fulfillmentWindow?.startDate ?? null;
    }

    return null;
};

export const titleToWorkflowStep = (title: string): VisitWorkflowStep | null => {
    const entry = Object.entries(AllVisitsPageNS.WorkflowStepTitles).find(([, t]) => t === title);
    return entry ? (entry[0] as VisitWorkflowStep) : null;
};

export const titlesToWorkflowSteps = (titles: string[]): VisitWorkflowStep[] => {
    return titles
        .map(titleToWorkflowStep)
        .filter((step): step is VisitWorkflowStep => step !== null);
};

export const isWorkflowStep = (tab: unknown): tab is VisitWorkflowStep =>
    typeof tab === 'string' && Object.values(VisitWorkflowStep).includes(tab as VisitWorkflowStep);
